import { motion, useInView } from 'framer-motion';
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useRef, useState } from 'react';

import { ACCENT_CYAN, MUTED_GRAY, NOPING_GREEN } from '../constants';

const RED = '#EF4444';

function generateHighPingPoints(_width: number, numPoints: number, seed: number): number[] {
  const points: number[] = [];
  for (let i = 0; i < numPoints; i += 1) {
    const base = 35 + Math.sin(i * 0.7 + seed) * 15 + Math.cos(i * 1.3 + seed * 0.5) * 10;
    const jitter = Math.sin(i * 3.7 + seed * 2.1) * 12;
    points.push(Math.max(10, Math.min(80, base + jitter)));
  }
  return points;
}

function generateLowPingPoints(numPoints: number, seed: number): number[] {
  const points: number[] = [];
  for (let i = 0; i < numPoints; i += 1) {
    const base = 150 + Math.sin(i * 0.5 + seed) * 3 + Math.cos(i * 0.8 + seed * 0.3) * 2;
    points.push(base);
  }
  return points;
}

function pointsToPath(points: number[], width: number): string {
  const step = width / (points.length - 1);
  return points.map((y, i) => `${i === 0 ? 'M' : 'L'}${(i * step).toFixed(1)},${y.toFixed(1)}`).join(' ');
}

export function PingSliderCompare() {
  const { t } = useTranslation(['components/trial/lp/v1b']);
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  const svgRef = useRef<SVGSVGElement>(null);
  const [sliderX, setSliderX] = useState(0.5);
  const [isDragging, setIsDragging] = useState(false);
  const [animSeed, setAnimSeed] = useState(0);

  useEffect(() => {
    if (!inView) return undefined;
    let raf: number;
    const animateSeed = () => {
      setAnimSeed((prev) => prev + 0.02);
      raf = requestAnimationFrame(animateSeed);
    };
    raf = requestAnimationFrame(animateSeed);
    return () => cancelAnimationFrame(raf);
  }, [inView]);

  const SVG_W = 600;
  const SVG_H = 180;
  const NUM_POINTS = 40;
  const dividerX = sliderX * SVG_W;

  const highPoints = generateHighPingPoints(SVG_W, NUM_POINTS, animSeed);
  const lowPoints = generateLowPingPoints(NUM_POINTS, animSeed);

  const highPath = pointsToPath(highPoints, SVG_W);
  const lowPath = pointsToPath(lowPoints, SVG_W);

  const highFill = `${highPath} L${SVG_W},${SVG_H} L0,${SVG_H} Z`;
  const lowFill = `${lowPath} L${SVG_W},${SVG_H} L0,${SVG_H} Z`;

  const avgHighPing = Math.round(highPoints.reduce((a, b) => a + b, 0) / highPoints.length);
  const displayHighPing = Math.round(avgHighPing * 1.8);
  const displayLowPing = Math.round(3 + Math.sin(animSeed * 0.5) * 2);

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isDragging || !svgRef.current) return;
      const rect = svgRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      setSliderX(Math.max(0.05, Math.min(0.95, x)));
    },
    [isDragging]
  );

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    setIsDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
    if (svgRef.current) {
      const rect = svgRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      setSliderX(Math.max(0.05, Math.min(0.95, x)));
    }
  }, []);

  const handlePointerUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  return (
    <div ref={ref} className='mx-auto w-full max-w-2xl'>
      <div className='mb-3 flex items-center justify-between px-2'>
        <div className='flex items-center gap-2'>
          <span className='h-0.5 w-3 rounded-full' style={{ backgroundColor: RED }} />
          <span className='font-mono text-xs' style={{ color: RED }}>
            {t('slider.without_noping')}: {displayHighPing}ms
          </span>
        </div>
        <div className='flex items-center gap-2'>
          <span className='h-0.5 w-3 rounded-full' style={{ backgroundColor: NOPING_GREEN }} />
          <span className='font-mono text-xs' style={{ color: NOPING_GREEN }}>
            {t('slider.with_noping')}: {displayLowPing}ms
          </span>
        </div>
      </div>

      <motion.div className='relative cursor-ew-resize overflow-hidden rounded-xl border' style={{ borderColor: `${ACCENT_CYAN}20`, backgroundColor: '#060608' }} initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.2 }}>
        <svg ref={svgRef} viewBox={`0 0 ${SVG_W} ${SVG_H}`} className='w-full' style={{ height: 'auto', aspectRatio: `${SVG_W}/${SVG_H}` }} preserveAspectRatio='none' onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={handlePointerUp} onPointerLeave={handlePointerUp}>
          <defs>
            <linearGradient id='v1b-redFill' x1='0' y1='0' x2='0' y2='1'>
              <stop offset='0%' stopColor={RED} stopOpacity='0.2' />
              <stop offset='100%' stopColor={RED} stopOpacity='0' />
            </linearGradient>
            <linearGradient id='v1b-greenFill' x1='0' y1='0' x2='0' y2='1'>
              <stop offset='0%' stopColor={NOPING_GREEN} stopOpacity='0.2' />
              <stop offset='100%' stopColor={NOPING_GREEN} stopOpacity='0' />
            </linearGradient>
            <clipPath id='v1b-clipLeft'>
              <rect x='0' y='0' width={dividerX} height={SVG_H} />
            </clipPath>
            <clipPath id='v1b-clipRight'>
              <rect x={dividerX} y='0' width={SVG_W - dividerX} height={SVG_H} />
            </clipPath>
            <filter id='v1b-glow'>
              <feGaussianBlur stdDeviation='2' result='blur' />
              <feMerge>
                <feMergeNode in='blur' />
                <feMergeNode in='SourceGraphic' />
              </feMerge>
            </filter>
          </defs>

          {Array.from({ length: 6 }).map((_, i) => (
            <line key={i} x1='0' y1={(i + 1) * 26} x2={SVG_W} y2={(i + 1) * 26} stroke='#ffffff' strokeWidth='0.5' opacity='0.04' />
          ))}

          <g clipPath='url(#v1b-clipLeft)'>
            <path d={highFill} fill='url(#v1b-redFill)' />
            <path d={highPath} fill='none' stroke={RED} strokeWidth='2' opacity='0.8' />
          </g>

          <g clipPath='url(#v1b-clipRight)'>
            <path d={lowFill} fill='url(#v1b-greenFill)' />
            <path d={lowPath} fill='none' stroke={NOPING_GREEN} strokeWidth='2.5' filter='url(#v1b-glow)' />
          </g>

          <line x1={dividerX} y1='0' x2={dividerX} y2={SVG_H} stroke='#ffffff' strokeWidth='1.5' opacity='0.6' strokeDasharray='4 4' />
          <circle cx={dividerX} cy={SVG_H / 2} r='12' fill='#ffffff' opacity='0.15' />
          <circle cx={dividerX} cy={SVG_H / 2} r='8' fill='#ffffff' opacity='0.3' />
          <path d={`M${dividerX - 4},${SVG_H / 2 - 3} L${dividerX - 7},${SVG_H / 2} L${dividerX - 4},${SVG_H / 2 + 3}`} stroke='#fff' strokeWidth='1.5' fill='none' strokeLinecap='round' />
          <path d={`M${dividerX + 4},${SVG_H / 2 - 3} L${dividerX + 7},${SVG_H / 2} L${dividerX + 4},${SVG_H / 2 + 3}`} stroke='#fff' strokeWidth='1.5' fill='none' strokeLinecap='round' />

          <text x={Math.max(dividerX / 2, 50)} y='20' textAnchor='middle' fill={RED} fontSize='10' fontFamily='monospace' fontWeight='bold' opacity='0.7'>
            {t('slider.without_noping_label')}
          </text>
          <text x={Math.min(dividerX + (SVG_W - dividerX) / 2, SVG_W - 50)} y='20' textAnchor='middle' fill={NOPING_GREEN} fontSize='10' fontFamily='monospace' fontWeight='bold' opacity='0.7'>
            {t('slider.with_noping_label')}
          </text>
        </svg>

        <motion.div className='absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full px-3 py-1 font-mono text-[10px]' style={{ color: MUTED_GRAY, backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }} animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 2, repeat: Infinity }}>
          {t('slider.drag_instruction')}
        </motion.div>
      </motion.div>
    </div>
  );
}
